import { useEffect, useMemo, useState } from 'react';
import {
  seatMapParter,
  seatMapBalcony,
  priceColors,
  getParterRowNumber,
  getBalconyRowNumber,
} from './seatMaps';

const cloneMap = (map) => JSON.parse(JSON.stringify(map));

const getRowIndex = (rowKey, typeSeat) => {
  const rowNumber = parseInt(rowKey, 10);
  if (Number.isNaN(rowNumber)) return null;
  if (typeSeat === 'Партер') {
    if (rowNumber >= 1 && rowNumber <= 10) return rowNumber - 1;
    if (rowNumber >= 11 && rowNumber <= 19) return rowNumber;
  }
  if (rowNumber >= 1 && rowNumber <= 4) return rowNumber - 1;
  if (rowNumber >= 5 && rowNumber <= 11) return rowNumber;
  return null;
};

/**
 * Помечает занятые/зарезервированные места статусом "taken".
 * - occupiedPlaces (проданные билеты): ключи — отображаемые номера рядов, значения — номера мест (label).
 * - reservedSeats (Redis-холды): ключи — индекс ряда карты, значения — индекс места в ряду.
 */
const markOccupiedSeats = (map, occupiedPlaces, reservedSeats, typeSeat) => {
  const seats = cloneMap(map);
  const occupied = occupiedPlaces?.[typeSeat] || {};
  const reserved = reservedSeats?.[typeSeat] || {};

  const apply = (places, status, keysAreMapRowIndex, valuesAreMapSeatIndex) => {
    Object.entries(places || {}).forEach(([rowKey, seatsList]) => {
      const rowIndex = keysAreMapRowIndex
        ? (() => {
            const n = parseInt(rowKey, 10);
            return Number.isNaN(n) ? null : n;
          })()
        : getRowIndex(rowKey, typeSeat);
      if (rowIndex === null || !seats[rowIndex] || !Array.isArray(seatsList)) return;
      seatsList.forEach((value) => {
        const seatIndex = seats[rowIndex].findIndex((seat, index) => {
          if (seat.status === 'gap') return false;
          return valuesAreMapSeatIndex
            ? Number(index) === Number(value)
            : Number(seat.label) === Number(value);
        });
        if (seatIndex !== -1) {
          seats[rowIndex][seatIndex].status = status;
        }
      });
    });
  };

  apply(reserved, 'taken', true, true);
  apply(occupied, 'taken', false, false);
  return seats;
};

const priceCategories = [600, 500, 400, 300];

function ResetZoomIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
      <path
        d="M3 12a9 9 0 0 1 15.3-6.36M21 3v6h-6M21 12a9 9 0 0 1-15.3 6.36M3 21v-6h6"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
    </svg>
  );
}

const normalizePrices = (prices) => {
  if (!Array.isArray(prices)) return [];
  return prices
    .map((price) => parseFloat(price))
    .filter((price) => Number.isFinite(price) && price >= 0);
};

const getSeatPrice = (seat, typeSeat, prices) => {
  if (prices.length >= 4) {
    const categoryIndex = priceCategories.indexOf(seat.price);
    return prices[categoryIndex] ?? seat.price;
  }
  if (prices.length >= 2) {
    return typeSeat === 'Балкон' ? prices[1] : prices[0];
  }
  return prices[0] ?? seat.price;
};

const getLegendItems = (prices) => {
  if (prices.length >= 4) {
    return priceCategories.map((category) => ({
      key: category,
      color: priceColors[category],
      label: `${getSeatPrice({ price: category }, category === 300 ? 'Балкон' : 'Партер', prices)} ₽`,
    }));
  }
  if (prices.length >= 2) {
    return [
      { key: 'parter', color: priceColors[600], label: `Партер ${prices[0]} ₽` },
      { key: 'balcony', color: priceColors[300], label: `Балкон ${prices[1]} ₽` },
    ];
  }
  return [{ key: 'all', color: priceColors[500], label: `${prices[0] ?? 0} ₽` }];
};

export function HallDK({ occupiedPlaces, reservedSeats, selectedSeats, setSeats, closeSelect, eventData }) {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [drag, setDrag] = useState(null);

  const eventPrices = useMemo(() => normalizePrices(eventData?.prices), [eventData?.prices]);
  const legendItems = useMemo(() => getLegendItems(eventPrices), [eventPrices]);

  const parterMap = useMemo(
    () => markOccupiedSeats(seatMapParter, occupiedPlaces, reservedSeats, 'Партер'),
    [occupiedPlaces, reservedSeats],
  );
  const balconyMap = useMemo(
    () => markOccupiedSeats(seatMapBalcony, occupiedPlaces, reservedSeats, 'Балкон'),
    [occupiedPlaces, reservedSeats],
  );

  useEffect(() => {
    const stopDrag = () => setDrag(null);
    window.addEventListener('mouseup', stopDrag);
    window.addEventListener('touchend', stopDrag);
    return () => {
      window.removeEventListener('mouseup', stopDrag);
      window.removeEventListener('touchend', stopDrag);
    };
  }, []);

  const isSelected = (typeSeat, rowIndex, seatIndex) =>
    selectedSeats?.some(
      (item) =>
        item.typeSeat === typeSeat &&
        Number(item.rowIndex ?? item.row) === Number(rowIndex) &&
        Number(item.seatIndex ?? item.seat) === Number(seatIndex),
    );

  const selectSeat = (seatData, typeSeat, rowIndex, seatIndex, rowNumber) => {
    if (!seatData || seatData.status === 'gap' || seatData.status === 'taken') return;

    const list = [...(selectedSeats || [])];
    const index = list.findIndex(
      (item) =>
        item.typeSeat === typeSeat &&
        Number(item.rowIndex ?? item.row) === Number(rowIndex) &&
        Number(item.seatIndex ?? item.seat) === Number(seatIndex),
    );

    if (index >= 0) {
      list.splice(index, 1);
    } else {
      if (closeSelect || list.length >= 5) return;
      list.push({
        rowIndex,
        seatIndex,
        row: rowIndex,
        seat: seatIndex,
        typeSeat,
        price: getSeatPrice(seatData, typeSeat, eventPrices),
        seatLabel: parseInt(seatData.label, 10),
        rowNumber,
        free: list.length < 2 && seatData.status === 'free-seat',
      });
    }
    setSeats(list);
  };

  const onPanStart = (event) => {
    const point = event.touches?.[0] || event;
    setDrag({ x: point.clientX, y: point.clientY, panX: pan.x, panY: pan.y });
  };

  const onPanMove = (event) => {
    if (!drag) return;
    const point = event.touches?.[0] || event;
    setPan({ x: drag.panX + point.clientX - drag.x, y: drag.panY + point.clientY - drag.y });
  };

  const renderSection = (title, map, typeSeat, getRowNumber) => (
    <div className="dk-section">
      <div className="dk-section__title">{title}</div>
      {title === 'Балкон' && (
        <div className="dk-section__warning">
          ВНИМАНИЕ! В первом ряду балкона ограниченное пространство для ног.
        </div>
      )}
      <div className="dk-rows">
        {map.map((row, rowIndex) => {
          const rowNumber = getRowNumber(rowIndex);
          if (rowNumber === null) {
            return <div className="dk-row dk-row--aisle" key={`${typeSeat}-${rowIndex}`} />;
          }
          return (
            <div className="dk-row" key={`${typeSeat}-${rowIndex}`}>
              <span className="dk-row__number">{rowNumber}</span>
              {row.map((seat, seatIndex) => {
                const selected = isSelected(typeSeat, rowIndex, seatIndex);
                const actualPrice = getSeatPrice(seat, typeSeat, eventPrices);
                const color = selected
                  ? '#4caf50'
                  : seat.status === 'taken'
                    ? '#aaaaaa'
                    : priceColors[seat.price] || '#dddddd';
                return (
                  <button
                    key={`${typeSeat}-${rowIndex}-${seatIndex}`}
                    type="button"
                    className={`dk-seat ${seat.status} ${selected ? 'is-selected' : ''}`.trim()}
                    style={{ '--price-color': color }}
                    disabled={seat.status === 'gap' || seat.status === 'taken' || (closeSelect && !selected)}
                    title={seat.status !== 'gap' ? `Ряд ${rowNumber}, Место ${seat.label}, ${actualPrice} ₽` : ''}
                    onClick={() => selectSeat(seat, typeSeat, rowIndex, seatIndex, rowNumber)}
                  >
                    {seat.status !== 'gap' ? seat.label : ''}
                  </button>
                );
              })}
              <span className="dk-row__number">{rowNumber}</span>
            </div>
          );
        })}
      </div>
    </div>
  );

  const isDefaultView = zoom === 1 && pan.x === 0 && pan.y === 0;

  return (
    <div className="hall-dk">
      <div className="hall-dk__toolbar">
        <div className="hall-dk__legend">
          {legendItems.map((item) => (
            <div className="hall-dk__legend-item" key={item.key}>
              <span className="hall-dk__legend-color" style={{ background: item.color }} />
              <span>{item.label}</span>
            </div>
          ))}
          <div className="hall-dk__legend-item">
            <span className="hall-dk__legend-color" style={{ background: '#4caf50' }} />
            <span>Выбрано</span>
          </div>
          <div className="hall-dk__legend-item">
            <span className="hall-dk__legend-color" style={{ background: '#aaaaaa' }} />
            <span>Занято</span>
          </div>
        </div>

        <div className="hall-dk__zoom">
          <button
            type="button"
            onClick={() => setZoom((value) => Math.max(0.5, +(value - 0.1).toFixed(1)))}
            disabled={zoom <= 0.5}
          >
            −
          </button>
          <input
            type="range"
            min="0.5"
            max="2"
            step="0.1"
            value={zoom}
            onChange={(event) => setZoom(parseFloat(event.target.value))}
          />
          <span>{Math.round(zoom * 100)}%</span>
          <button
            type="button"
            onClick={() => setZoom((value) => Math.min(2, +(value + 0.1).toFixed(1)))}
            disabled={zoom >= 2}
          >
            +
          </button>
          <button
            type="button"
            className="hall-dk__zoom-reset"
            aria-label="Сбросить масштаб"
            title="Сбросить"
            disabled={isDefaultView}
            onClick={() => {
              setZoom(1);
              setPan({ x: 0, y: 0 });
            }}
          >
            <ResetZoomIcon />
          </button>
        </div>
      </div>

      <div
        className="hall-dk__viewport"
        onMouseDown={onPanStart}
        onMouseMove={onPanMove}
        onTouchStart={onPanStart}
        onTouchMove={onPanMove}
      >
        <div className="hall-dk__stage">
          <div className="hall-dk__scene">Сцена</div>
          <div
            className="hall-dk__content"
            style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})` }}
          >
            {renderSection('Партер', parterMap, 'Партер', getParterRowNumber)}
            {renderSection('Балкон', balconyMap, 'Балкон', getBalconyRowNumber)}
          </div>
        </div>
      </div>
    </div>
  );
}
